import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, ArrowLeft, Linkedin, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import React from "react";

const TIER_COLOR = (v) => (v >= 80 ? "#007A5E" : v >= 60 ? "#DC1C22" : v >= 40 ? "#FFBF00" : "#FF2A00");

function HeaderBar({ ev, isWinner }) {
  const color = TIER_COLOR(ev.overall_score);
  return (
    <div className="p-5 border-b-2 border-zinc-900 relative" data-testid="compare-col-header">
      {isWinner && (
        <div className="absolute -top-3 right-3 bg-[#007A5E] text-white px-2 py-0.5 text-[9px] font-mono tracking-widest uppercase inline-flex items-center gap-1">
          <Trophy className="w-3 h-3" /> TOP
        </div>
      )}
      <div className="overline mb-2 inline-flex items-center gap-1.5">
        {ev.source_type === "linkedin" && <Linkedin className="w-3 h-3 text-[#0A66C2]" />}
        {ev.candidate_name || "Untitled"}
      </div>
      <div className="font-display font-bold text-base mb-3 truncate" title={ev.target_role}>
        {ev.target_role || (ev.source_type === "linkedin" ? "LinkedIn profile" : "No target role")}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-display font-extrabold text-5xl tabular-nums" style={{ color }}>{ev.overall_score}</span>
        <span className="text-xs text-zinc-400 font-mono">/100</span>
      </div>
      <div className="text-xs text-zinc-500 mt-2 font-mono">ATS · {ev.ats_score}/100</div>
      <Link to={`/results/${ev.id}`} className="block mt-3 text-xs text-zinc-500 hover:text-[#DC1C22] underline">
        Open full audit →
      </Link>
    </div>
  );
}

function RowLabel({ children }) {
  return <div className="p-4 overline border-b border-zinc-200/70 bg-zinc-50 sticky left-0" data-testid="compare-row-label">{children}</div>;
}

function ScoreCell({ value, best }) {
  const color = TIER_COLOR(value);
  return (
    <div className="p-4 border-b border-zinc-200/70 flex items-center gap-2">
      <span className="font-mono tabular-nums text-base" style={{ color }}>{value}</span>
      <span className="text-xs text-zinc-400">/100</span>
      {value === best && best > 0 && <Trophy className="w-3 h-3 text-[#007A5E]" />}
    </div>
  );
}

function ListCell({ items, color = "text-zinc-700", limit = 6 }) {
  if (!items || items.length === 0) return <div className="p-4 border-b border-zinc-200/70 text-xs text-zinc-400">—</div>;
  return (
    <div className="p-4 border-b border-zinc-200/70">
      <ul className={`text-xs space-y-1.5 ${color}`}>
        {items.slice(0, limit).map((it, i) => <li key={i}>• {it}</li>)}
        {items.length > limit && <li className="text-zinc-400 italic">+{items.length - limit} more</li>}
      </ul>
    </div>
  );
}

function ChipsCell({ items, tone = "neutral" }) {
  if (!items || items.length === 0) return <div className="p-4 border-b border-zinc-200/70 text-xs text-zinc-400">—</div>;
  const cls = tone === "success" ? "bg-emerald-50 text-emerald-900 border-emerald-200"
    : tone === "danger" ? "bg-red-50 text-red-900 border-red-200"
    : "bg-zinc-50 text-zinc-700 border-zinc-200";
  return (
    <div className="p-4 border-b border-zinc-200/70 flex flex-wrap gap-1.5">
      {items.slice(0, 12).map((c, i) => (
        <span key={i} className={`px-2 py-0.5 text-[11px] border ${cls}`}>{c}</span>
      ))}
      {items.length > 12 && <span className="text-[11px] text-zinc-400 italic self-center">+{items.length - 12}</span>}
    </div>
  );
}

export default function Compare() {
  const [params] = useSearchParams();
  const ids = (params.get("ids") || "").split(",").filter(Boolean);
  const [evals, setEvals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (ids.length < 2) { setLoading(false); return; }
      try {
        const { data } = await api.post("/resumes/compare", { ids });
        setEvals(data.evaluations || []);
      } catch (e) {
        toast.error("Could not load comparison", { description: e?.response?.data?.detail || e.message });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("ids")]);

  if (loading) {
    return <main className="min-h-[60vh] flex items-center justify-center" data-testid="compare-loading"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></main>;
  }
  if (!evals.length) {
    return (
      <main className="min-h-[60vh] flex flex-col items-center justify-center px-6">
        <p className="text-zinc-500 mb-4">Pick at least 2 audits from your history to compare.</p>
        <Link to="/history" className="btn-sharp bg-[#DC1C22] text-white">Back to history</Link>
      </main>
    );
  }

  const best = {
    overall: Math.max(...evals.map((e) => e.overall_score || 0)),
    ats: Math.max(...evals.map((e) => e.ats_score || 0)),
  };
  const categoryNames = Array.from(new Set(evals.flatMap((e) => (e.category_scores || []).map((c) => c.name))));
  const categoryBest = Object.fromEntries(
    categoryNames.map((n) => [
      n,
      Math.max(...evals.map((e) => (e.category_scores?.find((c) => c.name === n)?.score) || 0)),
    ])
  );

  const cols = evals.length;
  const winnerIdx = evals.findIndex((e) => e.overall_score === best.overall);

  // Tailwind cannot generate dynamic class names; map for known counts (2-4).
  const gridColsCls = { 2: "grid-cols-[200px_repeat(2,1fr)]", 3: "grid-cols-[200px_repeat(3,1fr)]", 4: "grid-cols-[200px_repeat(4,1fr)]" }[cols];

  return (
    <main className="min-h-screen bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50 text-zinc-900" data-testid="compare-page">
      <div className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <Link to="/history" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900" data-testid="back-to-history">
            <ArrowLeft className="w-4 h-4" /> Back to history
          </Link>
          <div className="text-xs text-zinc-500 font-mono">Comparing {cols} audits</div>
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="overline mb-4">◇ A/B COMPARE</div>
        <h1 className="font-display font-extrabold text-4xl lg:text-6xl tracking-tight mb-10 leading-[1.0]">
          Side-by-side.<br />Best wins.
        </h1>

        <div className={`grid ${gridColsCls} border border-zinc-200 bg-white overflow-x-auto`}>
          {/* Column headers */}
          <div className="p-5 border-b-2 border-zinc-900 bg-zinc-50">
            <div className="overline">DIMENSION</div>
          </div>
          {evals.map((ev, idx) => (
            <HeaderBar key={ev.id} ev={ev} isWinner={idx === winnerIdx} />
          ))}

          {/* Overall */}
          <RowLabel>Overall</RowLabel>
          {evals.map((ev) => <ScoreCell key={ev.id} value={ev.overall_score} best={best.overall} />)}

          {/* ATS */}
          <RowLabel>ATS</RowLabel>
          {evals.map((ev) => <ScoreCell key={ev.id} value={ev.ats_score} best={best.ats} />)}

          {/* Categories */}
          {categoryNames.map((name) => (
            <React.Fragment key={`cat-${name}`}>
              <RowLabel>{name}</RowLabel>
              {evals.map((ev) => {
                const score = ev.category_scores?.find((c) => c.name === name)?.score || 0;
                return <ScoreCell key={`${ev.id}-${name}`} value={score} best={categoryBest[name]} />;
              })}
            </React.Fragment>
          ))}

          {/* Strengths */}
          <RowLabel>Strengths</RowLabel>
          {evals.map((ev) => <ListCell key={`s-${ev.id}`} items={ev.strengths} color="text-emerald-800" />)}

          {/* Weaknesses */}
          <RowLabel>Weaknesses</RowLabel>
          {evals.map((ev) => <ListCell key={`w-${ev.id}`} items={ev.weaknesses} color="text-red-800" />)}

          {/* Keyword matches */}
          <RowLabel>Keywords matched</RowLabel>
          {evals.map((ev) => <ChipsCell key={`km-${ev.id}`} items={ev.keyword_matches} tone="success" />)}

          {/* Missing keywords */}
          <RowLabel>Missing keywords</RowLabel>
          {evals.map((ev) => <ChipsCell key={`mk-${ev.id}`} items={ev.missing_keywords} tone="danger" />)}

          {/* Skill gaps */}
          <RowLabel>Skill gaps</RowLabel>
          {evals.map((ev) => <ListCell key={`sg-${ev.id}`} items={ev.skill_gaps} />)}
        </div>
      </section>
    </main>
  );
}
